import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { fadeUp, viewport, stagger } from "@/lib/motion";
import {
  Building2,
  Landmark,
  Users2,
  Handshake,
  ArrowRight,
} from "lucide-react";

const WHATSAPP_NUMBER = "917861917281";

const partnersMsg =
  "Hi SuperAge Health, we'd like to explore partnering for a liver health program (assessment / awareness camp / keynote / executive wellness). Please share more details.";
const WA_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(partnersMsg)}`;

const AUDIENCES = [
  { icon: Building2, label: "Organizations" },
  { icon: Landmark, label: "Institutions" },
  { icon: Handshake, label: "Associations" },
  { icon: Users2, label: "Community Groups" },
];

export const Partners = () => {
  return (
    <section
      id="partners"
      className="relative overflow-hidden bg-sa-bg py-16 md:py-20"
      data-testid="partners-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
          {/* Left column — headline + copy */}
          <Reveal className="lg:col-span-6">
            <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
              Partner
            </p>
            <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-sa-forest sm:text-5xl">
              Partner with<br />SuperAge Health.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-sa-muted">
              SuperAge Health™ partners with organizations to deliver physician-led liver
              health assessments, executive wellness programs, health screenings, and
              educational sessions that promote early detection and prevention of liver
              disease. Our programs combine digital risk assessment, evidence-based
              screening, personalized guidance, and expert keynote presentations to help
              individuals understand their liver health and take proactive steps toward
              better long-term health.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-sa-muted">
              Whether you are an organization, institution, association, or community
              group, we can design a customized liver health program tailored to your
              audience.
            </p>

            <div className="mt-9">
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partners-whatsapp"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-sa-forest px-7 py-3.5 text-[15px] font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.03] hover:bg-sa-forest-soft"
              >
                Talk to the SuperAge team
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>

          {/* Right column — Programs + Who we partner with */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="flex flex-col gap-6 lg:col-span-6"
          >
            <motion.div variants={fadeUp} className="rounded-[24px] border border-sa-beige bg-white p-7 md:p-8">
              <p className="text-[12px] font-semibold uppercase tracking-[0.22em] text-sa-copper">
                Who we partner with
              </p>
              <ul className="mt-5 grid grid-cols-2 gap-3">
                {AUDIENCES.map((a) => {
                  const Icon = a.icon;
                  return (
                    <li
                      key={a.label}
                      className="flex items-center gap-3 rounded-2xl bg-sa-beige/50 px-4 py-3"
                    >
                      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-white text-sa-copper">
                        <Icon className="h-4.5 w-4.5" strokeWidth={1.7} />
                      </span>
                      <span className="text-[14px] font-medium text-sa-forest">{a.label}</span>
                    </li>
                  );
                })}
              </ul>
            </motion.div>

            <motion.div variants={fadeUp} className="rounded-[24px] bg-sa-forest p-7 md:p-8">
              <p className="text-[12px] font-semibold uppercase tracking-[0.22em] text-sa-copper">
                Ready to partner?
              </p>
              <p className="mt-3 font-display text-xl font-medium leading-snug text-white sm:text-2xl">
                To organize a liver health assessment, awareness campaign, health camp, or
                expert keynote session, contact the SuperAge Health™ team.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Partners;
